// M16 T21 — FormFill soru kartında KPI tutarlılık ipucu (PriorYearHint deseni):
// "KPI tablosunda 2025: 1.240 — KPI değerini kullan" (yerel setValue) ya da form dolu/KPI boşsa
// "KPI tablosuna yaz" (sunucu). Yalnız iç aktörde render edilir (FormFill guard).
// Finar 14.4 (K4): `unit_mismatch` → düğme YOK, yalnız "Birimler uyuşmuyor (kişi ↔ %)" notu;
// aynı aile farklı ölçek → düğme `suggestedValue` (soru biriminde) yazar + "çevrildi" notu.
import { useState } from "react";
import { formatKpiNumber, type KpiFormMismatch, type QuestionSnapshot } from "@mahir/shared";
import { applyFormToKpi } from "../../api/kpi-checks";
import { KPI_CHECKS_COPY as C } from "../../lib/kpi-checks-copy";

interface Props {
  entry: KpiFormMismatch;
  question: QuestionSnapshot;
  /**
   * Çıplak KPI sayısı SORU biriminde (`suggestedValue`; çevrilmişse çevrilmiş) — FormFill soru
   * tipine göre sayı (numeric) ya da TR metin olarak yazar (B-01).
   */
  onUseKpi: (value: number) => void;
  onWritten?: (() => void) | undefined;
}

export function KpiConsistencyHint({ entry, question, onUseKpi, onWritten }: Props): JSX.Element {
  const [busy, setBusy] = useState(false);
  if (entry.kind === "unit_mismatch") {
    return (
      <div
        className="mt-2 rounded-md px-3 py-2 text-xs"
        role="note"
        data-testid={`kpi-hint-${question.code}`}
        style={hintStyle}
      >
        <b>{entry.indicatorLabel}</b> ·{" "}
        {C.unitMismatchHint(entry.unit ?? C.unitless, entry.questionUnit ?? C.unitless)}
      </div>
    );
  }
  const suggested = entry.suggestedValue;
  const convertedText =
    entry.converted && entry.kpiValue !== null && suggested !== null
      ? C.converted(
          `${formatKpiNumber(entry.kpiValue)} ${entry.unit ?? ""}`.trim(),
          `${formatKpiNumber(suggested)} ${entry.questionUnit ?? ""}`.trim(),
        )
      : null;
  const kpiText = entry.kpiValue !== null ? formatKpiNumber(entry.kpiValue) : null;
  const formText = entry.formValue !== null ? formatKpiNumber(entry.formValue) : null;
  const text =
    kpiText !== null
      ? C.formHint(entry.year, `${kpiText}${entry.unit ? ` ${entry.unit}` : ""}`)
      : C.formOnlyForm(entry.year, formText ?? "—");
  return (
    <div
      className="mt-2 rounded-md px-3 py-2 text-xs flex flex-wrap items-center gap-2"
      role="note"
      data-testid={`kpi-hint-${question.code}`}
      style={hintStyle}
    >
      <span>
        <b>{entry.indicatorLabel}</b> · {text}
      </span>
      {suggested !== null && (
        <button type="button" className="underline font-medium" onClick={() => onUseKpi(suggested)}>
          {C.useKpi}
        </button>
      )}
      {convertedText !== null && (
        <span style={{ color: "var(--color-text-2)" }} data-testid="kpi-hint-converted">
          {convertedText}
        </span>
      )}
      {entry.kpiValue === null && entry.formValue !== null && (
        <button
          type="button"
          className="underline font-medium"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            applyFormToKpi(entry.projectId, entry.questionCode)
              .then(() => onWritten?.())
              .catch(() => undefined)
              .finally(() => setBusy(false));
          }}
        >
          {C.writeToKpi}
        </button>
      )}
    </div>
  );
}

const hintStyle = {
  background: "var(--color-warning-bg)",
  border: "1px solid var(--color-warning-border, var(--color-border))",
  color: "var(--color-text)",
} as const;
